// A05b — Magic-link reset landing (sub-plan 13 §13.4 UNAUTH route + §14.6).
// URL: /reset?token=<KV-token>
//
// Reads ?token= from useSearchParams; renders new password + confirm + submit
// with optional CaptchaWidget per sub-plan 14 §14.6 recommendation. POSTs
// /v1/auth/reset-password-by-token.
//
// Behavior on 200:
//   - target_role === "admin"  → navigate /login + show success toast
//                                ("Password reset. Please sign in.")
//   - target_role === "customer" | "engineer" → render "Open Find Geeks app"
//     landing INSTEAD of redirecting (admin /login rejects non-admin JWTs
//     per sub-plan 14 line 124-126).
//
// Belt-and-braces no-cache: <meta http-equiv="Cache-Control" content="no-store">
// is injected via document.head on mount as well as the /reset _headers entry.

import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tokens } from "@findgeeks/ui-tokens";
import { ENV } from "@/lib/env";
import {
  CaptchaWidget,
  type CaptchaWidgetHandle,
} from "@/components/CaptchaWidget";

function injectNoCacheMeta() {
  const metas: Array<[string, string]> = [
    ["Cache-Control", "no-store, no-cache, must-revalidate, max-age=0"],
    ["Pragma", "no-cache"],
    ["Expires", "0"],
  ];
  for (const [name, content] of metas) {
    if (
      !document.head.querySelector(
        `meta[http-equiv="${name}"]`,
      )
    ) {
      const meta = document.createElement("meta");
      meta.setAttribute("http-equiv", name);
      meta.setAttribute("content", content);
      document.head.appendChild(meta);
    }
  }
}

export function ResetPasswordByToken() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [appLanding, setAppLanding] = useState(false);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  useEffect(() => {
    injectNoCacheMeta();
  }, []);

  if (!token) {
    return (
      <CenteredCard>
        <Typography
          component="h1"
          sx={{
            fontSize: tokens.font.sizes.h2,
            fontWeight: tokens.font.weights.semibold,
            mb: 2,
          }}
        >
          Invalid reset link
        </Typography>
        <Alert severity="error" role="alert">
          This reset link is missing its token. Please request a new one from
          the Forgot Password page.
        </Alert>
      </CenteredCard>
    );
  }

  if (appLanding) {
    return (
      <CenteredCard>
        <Typography
          component="h1"
          sx={{
            fontSize: tokens.font.sizes.h1,
            fontWeight: tokens.font.weights.semibold,
            mb: 2,
          }}
        >
          Password reset
        </Typography>
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          Your password has been reset. Open the Find Geeks mobile app to sign
          in with your new password.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          (This admin web console is for administrators only. Mobile users
          should use the app.)
        </Typography>
      </CenteredCard>
    );
  }

  const passwordsMatch = pw.length > 0 && pw === pw2;
  const passwordLongEnough = pw.length >= 8;
  const canSubmit =
    passwordsMatch &&
    passwordLongEnough &&
    captchaToken !== null &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `${ENV.API_BASE_URL}/v1/auth/reset-password-by-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Captcha-Token": captchaToken!,
          },
          body: JSON.stringify({ token, new_password: pw }),
        },
      );
      captchaRef.current?.reset();
      setCaptchaToken(null);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message ?? body?.error ?? "Reset failed");
        return;
      }
      const body = (await res.json().catch(() => ({}))) as {
        target_role?: string;
      };
      if (body.target_role === "admin") {
        navigate("/login", {
          replace: true,
          state: { toast: "Password reset. Please sign in." },
        });
        return;
      }
      // customer/engineer or unknown → app landing.
      setAppLanding(true);
    } catch (err) {
      setError((err as Error).message ?? "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CenteredCard>
      <Typography
        component="h1"
        sx={{
          fontSize: tokens.font.sizes.h1,
          fontWeight: tokens.font.weights.semibold,
          mb: 3,
          color: tokens.colors.admin.textHeading,
        }}
      >
        Reset Password
      </Typography>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <TextField
          label="New password"
          type="password"
          fullWidth
          required
          autoComplete="new-password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          sx={{ mb: 2 }}
          helperText="At least 8 characters"
          error={pw.length > 0 && !passwordLongEnough}
        />
        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          required
          autoComplete="new-password"
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          sx={{ mb: 3 }}
          error={pw2.length > 0 && !passwordsMatch}
          helperText={
            pw2.length > 0 && !passwordsMatch ? "Passwords do not match" : " "
          }
        />

        <Box sx={{ mb: 2 }}>
          <CaptchaWidget
            ref={captchaRef}
            onToken={(t) => setCaptchaToken(t)}
            onError={() =>
              setError("Unable to verify captcha. Please try again.")
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!canSubmit}
          sx={{
            py: 1.25,
            borderRadius: 9999,
            background: tokens.colors.admin.primary,
            "&:hover": { background: tokens.colors.admin.primary },
          }}
        >
          {submitting ? <CircularProgress size={20} /> : "Reset password"}
        </Button>
      </Box>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#121212",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 450, p: 4 }}>
        {children}
      </Paper>
    </Box>
  );
}
